import React, { useEffect } from 'react';
import VehicleLookupPanel from './VehicleLookupPanel';

/**
 * VehicleLookupModal
 *
 * Drop-in modal version of the vehicle lookup. Designed to be opened from
 * places like Order Entry / PartFinder where the operator has a reg or VIN
 * but no make/model.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   ...
 *   {open && (
 *     <VehicleLookupModal
 *       isDark={isDark}
 *       onClose={() => setOpen(false)}
 *       onSelectVehicle={(v) => { setOpen(false); handleVehicle(v); }}
 *     />
 *   )}
 *
 * Props:
 *   isDark           (bool)
 *   onClose          (fn)   - close handler (X / overlay click / Esc)
 *   onSelectVehicle  (fn)   - called with the merged vehicle object when the
 *                             user picks "Use this vehicle". If omitted the
 *                             modal is read-only (no action button rendered).
 *   initialQuery     (string) - prefill (e.g. customer's previous reg) and auto-search
 *   title            (string) - default "Vehicle Lookup"
 */
export default function VehicleLookupModal({
  isDark = false,
  onClose,
  onSelectVehicle,
  initialQuery = '',
  title = 'Vehicle Lookup',
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 10050,
    padding: '4vh 16px',
    overflow: 'auto',
  };

  const panelStyle = {
    background: isDark ? '#1e293b' : '#ffffff',
    color: isDark ? '#e2e8f0' : '#1a3c5e',
    borderRadius: 12,
    width: '100%',
    maxWidth: 980,
    boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${isDark ? '#334155' : '#d6e2ed'}`,
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    borderBottom: `1px solid ${isDark ? '#334155' : '#e3ecf5'}`,
    background: isDark ? '#0f172a' : '#f8fbff',
  };

  const closeBtnStyle = {
    background: 'transparent',
    border: 'none',
    color: 'inherit',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    padding: '4px 10px',
    borderRadius: 6,
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div style={panelStyle}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close" style={closeBtnStyle}>×</button>
        </div>
        <div style={{ padding: '0.25rem 0.5rem 1rem' }}>
          <VehicleLookupPanel
            isDark={isDark}
            autoFocus
            compact
            initialQuery={initialQuery}
            onSelectVehicle={onSelectVehicle}
          />
        </div>
      </div>
    </div>
  );
}
